import { ConnectedPosition, Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  inject,
  OnChanges,
  OnDestroy,
  OnInit,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewChildren,
  ViewContainerRef,
} from '@angular/core';
import { MatMenuPanel, MatMenuTrigger } from '@angular/material/menu';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import isEqual from 'fast-deep-equal';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  EMPTY,
  filter,
  first,
  fromEvent,
  map,
  Observable,
  of,
  shareReplay,
  Subject,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import { BLAZE_CANVAS_SELECTOR, getHTMLElementForNode } from '@blaze-runtime/engine-domain';
import {
  type ElementHorizontalAlignment,
  getBackground,
  getBorderColor,
  makeFontStyles,
  makeFontStyleStyles,
  NavMenu,
  offsetBorderRadius,
} from '@blaze-shared/elements-domain';
import { isNullish, toInt } from '@blaze-shared/shared-util';

import { State } from '../../+state';
import { getElementsEntityState } from '../../+state/elements/elements.selectors';
import { isNavMenuItemReference, NavMenuItemsService } from '../../services/nav-menu-items.service';
import { findNavigationHostingOfElement } from '../../utils/canvas-element/path';
import { CanvasElementBaseComponent } from '../__base/base.component';

import {
  MenuRenderingContext,
  NavMenuIconPosition,
  NavMenuItemSize,
  NavMenuTemplate,
} from './nav-menu.models';
import { NavMenuService } from './nav-menu.service';

// TODO: test
@Component({
  selector: 'be-nav-menu',
  templateUrl: './nav-menu.component.html',
  styleUrls: ['./nav-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
@UntilDestroy()
export class NavMenuComponent
  extends CanvasElementBaseComponent<'navMenu'>
  implements OnChanges, OnInit, OnDestroy
{
  private readonly navMenuItemsService = inject(NavMenuItemsService);
  private readonly navMenuService = inject(NavMenuService);
  private readonly overlay = inject(Overlay);
  private readonly viewContainerRef = inject(ViewContainerRef);

  @ViewChild('navMenu') navMenuRef?: ElementRef;
  @ViewChild('mobileMenuTpl') mobileMenuTplRef?: TemplateRef<unknown>;
  @ViewChildren('menuItemTrigger') triggers!: QueryList<MatMenuTrigger>;

  @HostBinding('style.--b-nm-container-padding') containerPadding = '4px';
  @HostBinding('style.--b-nm-item-padding') itemPadding = '7px';
  @HostBinding('style.--b-nm-border-radius') borderRadius = '4px';
  @HostBinding('style.--b-nm-fixed-width') fixedWidth = '160px';
  @HostBinding('style.--b-nm-burger-color') burgerColor = '#000';

  private readonly visibleDropdownFor$$ = new BehaviorSubject<NavMenu.RenderItem | undefined>(
    undefined,
  );
  private readonly setMobileMenuOpen$$ = new Subject<boolean>();
  private mobileMenuOverlayRef?: OverlayRef;

  readonly elementKeyRoot = 'b-nm';
  readonly elementSubmenuKeyRoot = 'b-nm-dd';
  readonly elementMobileMenuKeyRoot = 'b-nm-mm';
  elementKey!: string;
  elementSubmenuKey!: string;
  elementMobileMenuKey!: string;

  showLineWhenActive = true;
  openedMatMenu: MatMenuTrigger | undefined;
  currentPageId$: Observable<string | undefined>;
  fontStyles!: Record<string, string | undefined>;

  readonly menuItems$: Observable<NavMenu.RenderItem[]>;
  /** Menu items without the ones which are excluded based on the visibility expression evaluated in runtime */
  readonly visibleMenuItems$: Observable<NavMenu.RenderItem[]>;
  readonly isVertical$: Observable<boolean>;
  readonly storageLoaded$: Observable<boolean>;
  readonly useCollapsedMobileMenu$: Observable<boolean>;
  readonly visibleDropdownFor$ = this.visibleDropdownFor$$.asObservable();
  readonly expandedSections = new Set<string>();

  menuTemplate: NavMenuTemplate | undefined = undefined;
  menuAlign: ElementHorizontalAlignment = 'left';
  menuItemSize: NavMenuItemSize = 'hug';
  iconPosition: NavMenuIconPosition = 'left';
  textAlign: ElementHorizontalAlignment = 'left';
  mobileMenuHeaderAlign = '';
  mobileMenuBurgerLabel = '';

  get version(): number {
    return this.props?.version ?? 1;
  }

  get isMobileMenuOpen(): boolean {
    return !!this.mobileMenuOverlayRef;
  }

  get isStickToBottom(): boolean {
    return this.elementContext?.navSection === 'bottom';
  }

  get menuClasses(): string[] {
    const classes: string[] = [
      `template-${this.menuTemplate ?? 'default'}`,
      `menu-align-${this.menuAlign}`,
      `text-align-${this.textAlign}`,
      `item-size-${this.menuItemSize}`,
      `icon-position-${this.iconPosition}`,
    ];

    if (this.isStickToBottom) {
      classes.push('stick-bottom');
    }

    if (this.showLineWhenActive) {
      classes.push('line-when-active');
    }

    return classes;
  }

  get areSectionsCollapsible(): boolean {
    return !!this.props?.content.sections.collapsible;
  }

  get showSectionDivider(): boolean {
    return !!this.props?.content.sections.showDivider;
  }

  constructor() {
    const store = inject<Store<State>>(Store);

    super();

    this.menuItems$ = this.navMenuItemsService.observeRenderMenuItems(
      this.props$.pipe(map((props) => props?.content.storage)),
    );

    this.currentPageId$ = store.select(runtimeSelectors.selectPageId);
    this.visibleMenuItems$ = combineLatest({
      menuItems: this.menuItems$,
      userAccessRoles: this.userAccessRolesService.userAccessRoles$,
    }).pipe(
      distinctUntilChanged(),
      switchMap(({ menuItems }) => (menuItems ? this.resolveVisibleMenuItems(menuItems) : of([]))),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.isVertical$ = this.props$.pipe(
      map((props) => props?.content?.direction === 'vertical'),
      distinctUntilChanged(),
    );

    this.useCollapsedMobileMenu$ = combineLatest({
      isMobile: this.viewportMode$.pipe(map((viewportMode) => viewportMode === 'mobile')),
      isVertical: this.isVertical$,
      enabled: this.props$.pipe(map((props) => !!props?.style.mobile?.enabled)),
    }).pipe(
      map(({ isMobile, isVertical, enabled }) => {
        if (isVertical || !enabled) {
          return false;
        }
        return isMobile;
      }),
      distinctUntilChanged(),
    );

    this.initializeClickOutside();

    // Initial selection
    combineLatest({
      currentPageId: this.currentPageId$,
      isVertical: this.isVertical$,
      visibleMenuItems: this.visibleMenuItems$,
    })
      .pipe(
        first(({ isVertical, visibleMenuItems }) => isVertical && visibleMenuItems.length > 0),
        map(({ currentPageId, isVertical, visibleMenuItems }) => {
          if (!isVertical) {
            return undefined;
          }

          return visibleMenuItems.find((item) =>
            this.hasActiveItem(item.children, currentPageId || ''),
          );
        }),
        untilDestroyed(this),
      )
      .subscribe((activeItem) => {
        if (activeItem) {
          // Expand default when vertical layout only
          this.visibleDropdownFor$$.next(activeItem);
        }
      });

    // Wait for storage to be loaded when there are references present in menu items
    this.storageLoaded$ = combineLatest({
      localStorage: this.props$.pipe(map((props) => props?.content.storage)),
      loaded: this.navMenuItemsService.storageLoaded$,
    }).pipe(
      map(({ localStorage, loaded }) => {
        if (loaded) {
          return true;
        }
        const items = localStorage ? Object.values(localStorage) : [];
        const hasReferences = items.some((item) => isNavMenuItemReference(item));
        return !hasReferences;
      }),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['props']) {
      if (!this.elementKey) {
        this.elementKey = `${this.elementKeyRoot}-${this.elementId}`;
        this.elementSubmenuKey = `${this.elementSubmenuKeyRoot}-${this.elementId}`;
        this.elementMobileMenuKey = `${this.elementMobileMenuKeyRoot}-${this.elementId}`;
      }

      const { content } = this.props || {};
      this.showLineWhenActive = content?.markSelected === true;

      this.updateStyles();
    }
  }

  ngOnInit(): void {
    this.stylesCleanup.register([
      this.elementKey,
      this.elementSubmenuKey,
      this.elementMobileMenuKey,
    ]);

    /**
     * Handle opening / closing menu on mobile
     */
    this.setMobileMenuOpen$$
      .pipe(
        untilDestroyed(this),
        tap((isOpen) => {
          if (!isOpen) {
            if (this.mobileMenuOverlayRef) {
              this.mobileMenuOverlayRef.detach();
              this.mobileMenuOverlayRef.dispose();
            }
            this.mobileMenuOverlayRef = undefined;
            this.cdr.detectChanges();
          }
        }),
        filter(Boolean),
        withLatestFrom(
          this.store.select(getElementsEntityState),
          this.element$,
          this.currentPageId$,
        ),
        tap(([, entityState, element, pageId]) => {
          const canvasElement = document.querySelector<HTMLElement>(BLAZE_CANVAS_SELECTOR);
          const menuElement = this.viewContainerRef.element.nativeElement;

          if (!canvasElement || !menuElement) {
            throw new Error('Could not find Canvas / Menu element');
          }

          const hosting = findNavigationHostingOfElement(
            entityState,
            element,
            ['navContainer', 'navBottombar'],
            pageId,
          );
          const hostingElement = hosting ? getHTMLElementForNode(hosting.id) : undefined;

          const overlayConfig: OverlayConfig = {
            hasBackdrop: true,
            panelClass: [this.elementMobileMenuKeyRoot, this.elementMobileMenuKey],
            backdropClass: 'b-nm-mm-bd',
            scrollStrategy: this.overlay.scrollStrategies.reposition(),
          };

          const position: ConnectedPosition = {
            originX: 'start',
            originY: this.isStickToBottom ? 'top' : 'bottom',
            overlayX: 'start',
            overlayY: this.isStickToBottom ? 'bottom' : 'top',
          };

          /**
           * Nav menu behaves differently when it is placed in nav container
           */
          if (hostingElement) {
            // Stick to bottom of nav container
            const positionStrategy = this.overlay
              .position()
              .flexibleConnectedTo(hostingElement)
              .withPush(false)
              .withPositions([position]);

            // Sync overlay size
            positionStrategy.positionChanges.subscribe(() => {
              overlayRef.hostElement.style.width = `${canvasElement.offsetWidth}px`;
              overlayRef.hostElement.style.maxHeight = `${window.innerHeight - hostingElement.offsetHeight}px`;
            });
            overlayConfig.positionStrategy = positionStrategy;
          } else {
            // Stick to bottom of nav menu
            const positionStrategy = this.overlay
              .position()
              .flexibleConnectedTo(menuElement)
              .withPush(false)
              .withGrowAfterOpen(true)
              .withPositions([position]);

            // Sync overlay size
            positionStrategy.positionChanges.subscribe(() => {
              overlayRef.hostElement.style.width = `${menuElement.offsetWidth}px`;
            });
            overlayConfig.positionStrategy = positionStrategy;
          }

          const overlayRef = this.overlay.create(overlayConfig);

          if (!this.mobileMenuTplRef) {
            throw new Error('No Menu content ref found');
          }

          const template = new TemplatePortal(this.mobileMenuTplRef, this.viewContainerRef);
          overlayRef.attach(template);

          overlayRef.backdropClick().subscribe(() => {
            this.setMobileMenuOpen$$.next(false);
          });

          this.mobileMenuOverlayRef = overlayRef;
          this.cdr.detectChanges();
        }),
      )
      .subscribe();

    /**
     * Try to close mobile menu when it no longer should be used
     */
    this.useCollapsedMobileMenu$.pipe(untilDestroyed(this)).subscribe((useCollapsedMobileMenu) => {
      if (!useCollapsedMobileMenu) {
        this.setMobileMenuOpen$$.next(false);
      }
    });
  }

  ngOnDestroy(): void {
    this.stylesCleanup.dispose([
      this.elementKey,
      this.elementSubmenuKey,
      this.elementMobileMenuKey,
    ]);
  }

  onItemClick(event: MouseEvent, item: NavMenu.RenderItem): void {
    this.navMenuService.handleItemClick(item);
    if (this.props?.content.direction === 'horizontal') {
      this.onMenuToggle(event);
    }
  }

  onMenuToggle(event: MouseEvent, item?: NavMenu.RenderItem): void {
    this.openedMatMenu?.closeMenu();
    this.openedMatMenu = this.triggers.find((t) => t.menuOpen);

    const buttonHtmlElement = event.currentTarget as HTMLElement;
    const menuPanelHtmlElement = document.getElementById(this.openedMatMenu?.menu?.panelId ?? '');

    if (menuPanelHtmlElement) {
      menuPanelHtmlElement.style.minWidth = `${buttonHtmlElement.offsetWidth}px`;
    }

    this.visibleDropdownFor$$.next(
      this.isSameItem(item, this.visibleDropdownFor$$.value) ? undefined : item,
    );
  }

  onSectionClick(item: NavMenu.RenderItem): void {
    if (!this.areSectionsCollapsible) {
      return;
    }

    if (this.expandedSections.has(item.id)) {
      this.expandedSections.delete(item.id);
    } else {
      this.expandedSections.add(item.id);
    }
  }

  isSameItem(
    itemA: NavMenu.RenderItem | undefined,
    itemB: NavMenu.RenderItem | undefined,
  ): boolean {
    return isEqual(itemA, itemB);
  }

  toggleMobileMenuOpen(): void {
    this.setMobileMenuOpen$$.next(!this.isMobileMenuOpen);
  }

  hasIcons(items?: NavMenu.RenderItem[], type?: NavMenu.ItemType): boolean {
    if (!type) {
      return items?.some((item) => item.type !== 'section' && item.props?.icon) ?? false;
    }

    return items?.some((item) => item.type === type && item.props?.icon) ?? false;
  }

  menuHasIconClass(
    item: NavMenu.RenderItem,
    context: MenuRenderingContext,
    parentHasIcons?: boolean,
  ): boolean {
    return !!item.props?.icon || (context.isVertical && !!parentHasIcons);
  }

  menuIsActiveClass(item: NavMenu.RenderItem, context: MenuRenderingContext): boolean {
    return (
      this.isActiveItem(item, context.currentPageId) ||
      (!this.isSameItem(context.visibleDropdownFor, item) &&
        this.hasActiveItem(item.children, context.currentPageId))
    );
  }

  menuShouldShowIcon(
    item: NavMenu.RenderItem,
    context: MenuRenderingContext,
    parentHasIcons?: boolean,
  ): boolean {
    return (
      !!item.props?.icon || (context.isVertical && this.iconPosition === 'left' && !!parentHasIcons)
    );
  }

  submenuHasIconClass(
    item: NavMenu.RenderItem,
    subitem: NavMenu.RenderItem,
    context: MenuRenderingContext,
    hasIcons: boolean,
  ): boolean {
    return (
      !!subitem.props?.icon ||
      hasIcons ||
      !!(
        !context.isVertical &&
        item.props?.label &&
        item.props?.icon &&
        this.iconPosition !== 'top'
      )
    );
  }

  submenuIsActiveClass(subitem: NavMenu.RenderItem, context: MenuRenderingContext): boolean {
    return this.isActiveItem(subitem, context.currentPageId);
  }

  submenuShouldShowIcon(
    item: NavMenu.RenderItem,
    subitem: NavMenu.RenderItem,
    context: MenuRenderingContext,
    hasIcons: boolean,
  ): boolean {
    return (
      !!subitem.props?.icon ||
      hasIcons ||
      !!(
        !context.isVertical &&
        !context.useCollapsedMobileMenu &&
        item.props?.label &&
        item.props?.icon &&
        this.iconPosition !== 'top'
      )
    );
  }

  getMatMenuTriggerTarget(
    item: NavMenu.RenderItem,
    context: MenuRenderingContext,
    panel: MatMenuPanel,
  ): MatMenuPanel | null {
    return item.children?.length && !context.isVertical && !context.useCollapsedMobileMenu
      ? panel
      : null;
  }

  getShouldAddDivider(item: NavMenu.RenderItem, nextItem: NavMenu.RenderItem | undefined): boolean {
    if (item.type === 'section' || nextItem?.type !== 'section') {
      return false;
    }

    return !!nextItem.children?.length;
  }

  /**
   * Is menu item active
   */
  private isActiveItem(item: NavMenu.RenderItem, currentPageId: string): boolean {
    return item.props?.clickAction === 'GoToPage' && item.props?.actionParams === currentPageId;
  }

  /**
   * Are there any active submenu items
   */
  private hasActiveItem(items: NavMenu.RenderItem[] = [], currentPageId: string): boolean {
    return items.some((item) => this.isActiveItem(item, currentPageId));
  }

  private initializeClickOutside(): void {
    this.visibleDropdownFor$$
      .pipe(
        withLatestFrom(this.isVertical$),
        filter(([, isVertical]) => !isVertical),
        switchMap(([item]) => (item !== undefined ? fromEvent(document, 'click') : EMPTY)),
        untilDestroyed(this),
      )
      .subscribe((event) => {
        if (!this.navMenuRef?.nativeElement.contains(event.target)) {
          this.openedMatMenu?.closeMenu();
          this.openedMatMenu = undefined;
          this.visibleDropdownFor$$.next(undefined);
        }
      });
  }

  private updateStyles(): void {
    const { style, content } = this.props || {};
    const { font } = content || {};
    const {
      active,
      default: def,
      general,
      hover,
      labels,
      menu,
      mobile,
      outline,
      submenu,
      sections,
    } = style || {};
    if (
      !active ||
      !content ||
      !def ||
      !font ||
      !general ||
      !hover ||
      !menu ||
      !mobile ||
      !outline ||
      !style ||
      !submenu ||
      !sections
    ) {
      return;
    }

    const mb = { default: def, hover, active, outline };
    const smb = {
      default: submenu.default,
      hover: submenu.hover,
      active: submenu.active,
      outline: submenu.outline,
    };

    this.fontStyles = {
      ...makeFontStyles(font),
      ...makeFontStyleStyles(font.style),
      ['font-weight']: this.fontsService.getFontWeight(font.fontFamily, font.fontWeight),
    };

    this.menuTemplate = content.template;
    this.menuAlign = content.align ?? 'left';
    this.menuItemSize = content.itemSize ?? 'hug';
    this.iconPosition = content.iconPosition ?? 'left';
    this.textAlign = labels?.align ?? 'left';
    this.mobileMenuHeaderAlign = mobile?.header?.align === 'right' ? 'flex-end' : 'flex-start';
    this.mobileMenuBurgerLabel = mobile.label;

    /**
     * Version 2
     * Padding knob is now changing padding in menu item instead of entire container.
     */
    if (this.version > 1) {
      this.containerPadding = '0px';
      this.itemPadding = general.offset.padding ?? '7px';
    } else {
      this.containerPadding = general.offset.padding ?? '4px';
      this.itemPadding = '7px';
    }

    this.borderRadius = outline.borderRadius;
    this.fixedWidth = content.itemWidth ?? '160px';
    this.burgerColor = mobile.burger.color.color1;

    let menuIconSize = menu.iconSize;
    let menuIconSizeCollapsed = menu.iconSize;
    let submenuIconSize = submenu.iconSize;
    if (isNullish(menuIconSize) || isNullish(menuIconSizeCollapsed)) {
      menuIconSize =
        this.menuTemplate === 'header' ? `${toInt(font.fontSize, 0) + 10}px` : font.fontSize;

      menuIconSizeCollapsed = font.fontSize;
    }
    if (isNullish(submenuIconSize)) {
      submenuIconSize = font.fontSize;
    }

    this.stylesCleanup.update(
      this.elementKey,
      `
    .${this.elementKey}.menu,
    .${this.elementMobileMenuKey} {
      --b-nm-mb-d-c: ${mb.default.text.color1};
      --b-nm-mb-d-b: ${getBackground(mb.default.background)};
      --b-nm-mb-d-bc: ${getBorderColor(mb.default.border)};
      --b-nm-mb-d-ic: ${mb.default.iconColor?.color1};
      --b-nm-mb-h-c: ${mb.hover.text.color1};
      --b-nm-mb-h-b: ${getBackground(mb.hover.background)};
      --b-nm-mb-h-bc: ${getBorderColor(mb.hover.border)};
      --b-nm-mb-h-ic: ${mb.hover.iconColor?.color1};
      --b-nm-mb-a-c: ${mb.active.text.color1};
      --b-nm-mb-a-b: ${getBackground(mb.active.background)};
      --b-nm-mb-a-bc: ${getBorderColor(mb.active.border)};
      --b-nm-mb-a-ic: ${mb.active.iconColor?.color1};
      --b-nm-mb-border-width: ${mb.outline.borderWidth};
      --b-nm-mb-chevron-size: ${font.fontSize};
      --b-nm-mb-icon-size: ${menuIconSize};
      --b-nm-mb-icon-size-collapsed: ${menuIconSizeCollapsed};
      --b-nm-sc-title-color: ${sections.label.text.color1};
      --b-nm-sc-divider-height: ${sections.divider.height};
      --b-nm-sc-divider-color: ${sections.divider.line.color1};
      --b-nm-sc-title-margin: ${sections.titleMargin};
      --b-nm-sc-divider-margin: ${sections.sectionMargin};
    }

    .${this.elementSubmenuKey} {
      --b-nm-dd-border-radius: ${outline.borderRadius};
      --b-nm-dd-outer-border-radius: ${offsetBorderRadius(outline.borderRadius, 4)};
    }

    .${this.elementSubmenuKey}.submenu,
    .${this.elementMobileMenuKey} {
      --b-nm-smb-d-c: ${smb.default.text.color1};
      --b-nm-smb-d-b: ${getBackground(smb.default.background)};
      --b-nm-smb-d-bc: ${getBorderColor(smb.default.border)};
      --b-nm-smb-d-ic: ${smb.default.iconColor?.color1};
      --b-nm-smb-h-c: ${smb.hover.text.color1};
      --b-nm-smb-h-b: ${getBackground(smb.hover.background)};
      --b-nm-smb-h-bc: ${getBorderColor(smb.hover.border)};
      --b-nm-smb-h-ic: ${smb.hover.iconColor?.color1};
      --b-nm-smb-a-c: ${smb.active.text.color1};
      --b-nm-smb-a-b: ${getBackground(smb.active.background)};
      --b-nm-smb-a-bc: ${getBorderColor(smb.active.border)};
      --b-nm-smb-a-ic: ${smb.active.iconColor?.color1};
      --b-nm-smb-border-width: ${smb.outline.borderWidth};
      --b-nm-smb-background: ${getBackground(submenu.background)};
      --b-nm-border-radius: ${outline.borderRadius};
      --b-nm-submenu-outer-border-radius: ${offsetBorderRadius(outline.borderRadius, 4)};
      --b-nm-mb-chevron-size: ${font.fontSize};
      --b-nm-smb-icon-size: ${submenuIconSize};
      --b-nm-item-padding: ${this.itemPadding};
    }

    .${this.elementKey}.horizontal .menu-button.active:after {
      bottom: -${outline.borderWidth} !important;
      left: -${outline.borderWidth} !important;
      width: calc(100% + 2 * ${outline.borderWidth});
    }
    .${this.elementKey}.vertical .menu-button.active:after {
      bottom: -${outline.borderWidth} !important;
      left: -${outline.borderWidth} !important;
      height: calc(100% + 2 * ${outline.borderWidth});
    }
    .${this.elementMobileMenuKey}  {
      background: ${getBackground(mobile.overlay.background)};
    }
    `,
    );
  }

  private resolveVisibleMenuItems(
    allItems: NavMenu.RenderItem[],
  ): Observable<NavMenu.RenderItem[]> {
    if (allItems.length === 0) {
      return of([]);
    }

    const items = allItems.filter((item) => {
      return (
        !item.props?.visible?.accessRoles?.length ||
        this.userAccessRolesService.isCurrentUserInAccessRoles(item.props?.visible.accessRoles)
      );
    });

    if (!items.length) {
      return of([]);
    }

    const resolvedItems$ = items.map((item) => this.resolveMenuItemVisibility(item));
    return combineLatest(resolvedItems$).pipe(
      map((resolvedItems) => {
        // filter out invisible fields
        return resolvedItems.filter((item) => !!item);
      }),
    );
  }

  private resolveMenuItemVisibility(
    item: NavMenu.RenderItem,
  ): Observable<NavMenu.RenderItem | undefined> {
    const subItems$: Observable<NavMenu.RenderItem[] | undefined> = item.children
      ? this.resolveVisibleMenuItems(item.children)
      : of(undefined);

    const isCurrentVisible$: Observable<boolean> = item.props?.visibilityExpression
      ? this.runtimeContext$.pipe(
          map((context) => !!this.evaluateExpression(item.props?.visibilityExpression, context)),
        )
      : of(true);

    return isCurrentVisible$.pipe(
      distinctUntilChanged(),
      switchMap((isVisible) => {
        if (!isVisible) {
          return of(undefined);
        }

        return subItems$.pipe(
          map((subItems) => ({
            ...item,
            children: subItems as any,
          })),
        );
      }),
    );
  }
}
